import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'
import { Cell, Column, SortingRule } from 'react-table'
import { useIntl } from 'react-intl'

import { TableWithPaginationWrapper } from 'components/Table/styles'
import { MobileSortBy, Table, CommonSearch } from 'components/Table'
import { SelectedDealerBtn, TransferLicenseTableWrapper } from '../styles'
import type { DomainType } from 'features/domains/types'
import TransferLicenseMobile from './TransferLicenseMobile'
import { homeLicensesMessages } from '../../../messages'
import {
  ReclaimLicenseContentTitle,
  ReviewDealerEmail,
  ReviewDealerNameCellContainter,
} from '../../ReclaimLicenseModal/styles'
import { makePublicUrl } from 'app/utils/publicUrl'
import CompanyImage from 'components/Image/CompanyImage'
import { getSortOptions } from 'components/Table/utils'

export type DealerChangeDispatchType = Dispatch<
  SetStateAction<DomainType | null>
>

const DEFAULT_SORT = {
  id: 'name',
  desc: false,
}

interface TransferDealerSelectedCellProps {
  dealer: DomainType
  targetDealer: DomainType | null
}

export function TransferDealerSelectedCell(
  props: TransferDealerSelectedCellProps
) {
  const isSelected: boolean =
    !!props.dealer &&
    !!props.targetDealer &&
    props.dealer.id === props.targetDealer.id

  return <SelectedDealerBtn $isSelected={isSelected}></SelectedDealerBtn>
}

export function TransferDealerNameCell({ dealer }: { dealer: DomainType }) {
  return (
    <ReviewDealerNameCellContainter>
      <CompanyImage
        mini
        src={
          dealer.imageUrl || makePublicUrl('images/defaultRootDomainIcon.svg')
        }
      />
      <ReclaimLicenseContentTitle>{dealer.name}</ReclaimLicenseContentTitle>
    </ReviewDealerNameCellContainter>
  )
}

export function TransferDealerEmailCell({ email }: { email: string }) {
  if (!email) return null

  return <ReviewDealerEmail>{email}</ReviewDealerEmail>
}

interface TransferLicenseTableProps {
  dealers: DomainType[]
  targetDealer: DomainType | null
  onTargetDealerChange: DealerChangeDispatchType
}

/**
 * @todo localization
 */

function TransferLicenseTable({
  dealers,
  targetDealer,
  onTargetDealerChange,
}: TransferLicenseTableProps) {
  const { formatMessage } = useIntl()

  const headers = useMemo(
    () => ({
      name: {
        label: 'Name',
        id: 'name',
      },
      phone: {
        label: 'Phone',
        id: 'phone',
      },
      email: {
        label: 'Email',
        id: 'email',
      },
    }),
    []
  )

  const sortOptions = useMemo(() => getSortOptions(headers), [headers])

  const columns: readonly Column<DomainType>[] = useMemo(
    () => [
      {
        Header: '',
        id: 'selected-target-dealer',
        Cell: (cell: Cell<DomainType>) => (
          <TransferDealerSelectedCell
            {...cell}
            dealer={cell.row.original}
            targetDealer={targetDealer}
          />
        ),
        percentWidth: '0.1%',
      },
      {
        Header: headers.name.label,
        id: headers.name.id,
        accessor: 'name',
        Cell: (cell: Cell<DomainType>) => (
          <TransferDealerNameCell dealer={cell.row.original} />
        ),
        percentWidth: '40%',
      },
      {
        Header: headers.phone.label,
        id: headers.phone.id,
        accessor: 'phone',
        percentWidth: '25%',
      },
      {
        Header: headers.email.label,
        id: headers.email.id,
        accessor: 'email',
        percentWidth: '25%',
        Cell: (cell: Cell<DomainType>) => (
          <TransferDealerEmailCell email={cell.value} />
        ),
      },
    ],
    [
      headers.email.id,
      headers.email.label,
      headers.name.id,
      headers.name.label,
      headers.phone.id,
      headers.phone.label,
      targetDealer,
    ]
  )

  const [sortBy, setSortBy] = useState<SortingRule<DomainType>>(DEFAULT_SORT)
  const [searchValue, setSearchValue] = useState<string>('')

  useEffect(() => {
    return () => {
      onTargetDealerChange(null)
    }
  }, [onTargetDealerChange])

  return (
    <TableWithPaginationWrapper>
      <TransferLicenseTableWrapper>
        <CommonSearch
          disableQuerySearch
          externalSearchValue={searchValue}
          setSearchValue={setSearchValue}
          placeholder={formatMessage(
            homeLicensesMessages.transferSearchPlaceholder
          )}
        />

        <br />

        <MobileSortBy
          headers={headers}
          sortOptions={sortOptions}
          sortBy={sortBy}
          setSortBy={setSortBy}
        />
        <Table
          columns={columns}
          data={dealers}
          searchValue={searchValue}
          onRowSelect={(rowProps) => {
            onTargetDealerChange(rowProps.original)
          }}
          mobileViewComponent={(item) => (
            <TransferLicenseMobile {...item} targetDealer={targetDealer} />
          )}
          defaultSortBy={DEFAULT_SORT}
          sortBy={sortBy}
          setSortBy={setSortBy}
        />
      </TransferLicenseTableWrapper>
    </TableWithPaginationWrapper>
  )
}

export default TransferLicenseTable
